import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import CelebrationModal from '../../components/BridgeClub/CelebrationModal';
import { bridgeClubAPI, ExtractedReceiptData } from '../../services/bridgeClubAPI';
import './BridgeClubUploadReceipt.css';

type Step = 'upload' | 'scanning' | 'confirm' | 'success';

interface CelebrationData {
  earned_reward: boolean;
  reward_code: string;
  reward_description: string;
  expires_at: string;
  member_name: string;
  store_name: string | null;
}

const BridgeClubUploadReceipt: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedReceiptData | null>(null);
  const [matchedStoreId, setMatchedStoreId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState<any>(null);

  // Celebration modal state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState<CelebrationData | null>(null);

  /**
   * Compress an image file using canvas before upload.
   * iPhone photos are typically 3-8 MB and timeout the gunicorn worker.
   * Resize to max 1200px wide and JPEG quality 70% — usually <200 KB.
   */
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const maxWidth = 1200;
          const ratio = Math.min(maxWidth / img.width, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // toDataURL returns "data:image/jpeg;base64,..."
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = reader.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    try {
      // Compress before upload — iPhone photos are huge and timeout the worker
      const compressedDataUrl = await compressImage(file);
      setImagePreview(compressedDataUrl);

      // Extract base64 (remove data URL prefix)
      const base64 = compressedDataUrl.split(',')[1];
      setImageBase64(base64);

      // Start scanning
      await scanReceipt(base64);
    } catch (err) {
      console.error('Image compression failed:', err);
      setError('Failed to process image. Please try a different photo.');
    }
  };

  const scanReceipt = async (base64: string) => {
    setStep('scanning');
    setError(null);

    try {
      const result = await bridgeClubAPI.uploadReceipt(base64);
      setExtractedData(result.extracted_data);
      setMatchedStoreId(result.matched_store_id);
      setStep('confirm');
    } catch (err: any) {
      setError(err?.detail || 'Failed to scan receipt. Please try again.');
      setStep('upload');
    }
  };

  const handleConfirm = async () => {
    if (!extractedData || !imageBase64) return;

    setSaving(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.confirmPurchase({
        extracted_data: extractedData,
        store_id: matchedStoreId || undefined,
        purchase_date: extractedData.receipt_date || new Date().toISOString().split('T')[0],
        items: extractedData.items,
        receipt_image: imageBase64,
        needs_verification: false,
      });

      setSavedResult(result);

      // Check if this purchase earned a reward - show celebration!
      if (result.celebration?.earned_reward) {
        setCelebrationData(result.celebration);
        setShowCelebration(true);
      }

      setStep('success');
    } catch (err: any) {
      // Handle duplicate receipt errors with specific messages
      const errorCode = err?.error || err?.code;
      const errorMessage = err?.message || err?.detail;

      if (errorCode === 'receipt_already_redeemed') {
        setError('This receipt has already been used by another account. Each receipt can only be redeemed once.');
      } else if (errorCode === 'duplicate_receipt') {
        setError(errorMessage || 'You have already scanned this receipt.');
      } else {
        setError(errorMessage || 'Failed to save purchase. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCelebration = () => {
    setShowCelebration(false);
  };

  const handleItemEdit = (index: number, field: string, value: any) => {
    if (!extractedData?.items) return;

    const newItems = [...extractedData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    setExtractedData({
      ...extractedData,
      items: newItems,
    });
  };

  const handleRemoveItem = (index: number) => {
    if (!extractedData?.items) return;

    const newItems = extractedData.items.filter((_, i) => i !== index);
    setExtractedData({
      ...extractedData,
      items: newItems,
    });
  };

  const renderUploadStep = () => (
    <div className="upload-step">
      <div className="upload-header">
        <h1>Scan Receipt</h1>
        <p>Take a photo or upload an image of your receipt to log your purchase</p>
      </div>

      <div
        className="upload-area"
        onClick={() => fileInputRef.current?.click()}
      >
        <span className="upload-icon">📸</span>
        <span className="upload-text">Tap to upload receipt</span>
        <span className="upload-hint">JPG, PNG, or HEIC</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden-input"
      />

      {error && <div className="error-message">{error}</div>}

      <div className="upload-tips">
        <h3>Tips for best results:</h3>
        <ul>
          <li>Make sure the receipt is flat and well-lit</li>
          <li>Include the entire receipt in the photo</li>
          <li>Avoid shadows and glare</li>
        </ul>
      </div>
    </div>
  );

  const renderScanningStep = () => (
    <div className="scanning-step">
      {imagePreview && (
        <div className="receipt-preview">
          <img src={imagePreview} alt="Receipt" />
          <div className="scanning-overlay">
            <div className="scanning-animation"></div>
            <span>Scanning receipt...</span>
          </div>
        </div>
      )}
      <p className="scanning-text">
        Our AI is reading your receipt and identifying Red Bridge products...
      </p>
    </div>
  );

  const renderConfirmStep = () => {
    const redBridgeItems = (extractedData?.items || []).filter(item => item.is_red_bridge);
    const otherItems = (extractedData?.items || []).filter(item => !item.is_red_bridge);

    return (
      <div className="confirm-step">
        <h1>Review Purchase</h1>

        {error && <div className="error-message">{error}</div>}

        {/* Receipt Preview */}
        {imagePreview && (
          <div className="mini-preview" onClick={() => setStep('upload')}>
            <img src={imagePreview} alt="Receipt" />
            <span>Retake</span>
          </div>
        )}

        {/* Receipt Info */}
        <div className="receipt-info">
          {extractedData?.store_name && (
            <div className="info-row">
              <span className="info-label">Store</span>
              <span className="info-value">{extractedData.store_name}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Date</span>
            <input
              type="date"
              value={extractedData?.receipt_date || ''}
              onChange={(e) => setExtractedData({ ...extractedData!, receipt_date: e.target.value })}
              className="date-input"
            />
          </div>
        </div>

        {/* Red Bridge Items */}
        {redBridgeItems.length > 0 && (
          <div className="items-section">
            <h3>🎉 Red Bridge Products ({redBridgeItems.length})</h3>
            <p className="items-note">These count toward your loyalty rewards!</p>
            {redBridgeItems.map((item, idx) => {
              const originalIdx = (extractedData?.items || []).indexOf(item);
              return (
                <div key={idx} className="item-card red-bridge">
                  <div className="item-header">
                    <span className="item-name">{item.name}</span>
                    <button
                      onClick={() => handleRemoveItem(originalIdx)}
                      className="remove-btn"
                    >×</button>
                  </div>
                  <div className="item-details">
                    <div className="item-field">
                      <label>Qty</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.quantity}
                        onChange={(e) => handleItemEdit(originalIdx, 'quantity', e.target.value)}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          handleItemEdit(originalIdx, 'quantity', Math.max(1, val));
                        }}
                      />
                    </div>
                    <div className="item-field">
                      <label>Price</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={item.total_price}
                        onChange={(e) => handleItemEdit(originalIdx, 'total_price', e.target.value)}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          handleItemEdit(originalIdx, 'total_price', Math.max(0, val));
                        }}
                      />
                    </div>
                  </div>
                  {item.matched_product_name && (
                    <div className="matched-product">
                      Matched: {item.matched_product_name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Other Items */}
        {otherItems.length > 0 && (
          <div className="items-section">
            <h3>Other Items ({otherItems.length})</h3>
            {otherItems.map((item, idx) => {
              const originalIdx = (extractedData?.items || []).indexOf(item);
              return (
                <div key={idx} className="item-card">
                  <div className="item-header">
                    <span className="item-name">{item.name}</span>
                    <button
                      onClick={() => handleRemoveItem(originalIdx)}
                      className="remove-btn"
                    >×</button>
                  </div>
                  <div className="item-details">
                    <span className="item-qty">{item.quantity}x</span>
                    <span className="item-price">${Number(item.total_price || 0).toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Discounts Section */}
        {extractedData?.discounts && extractedData.discounts.length > 0 && (
          <div className="discounts-section">
            <h3>Discounts Applied</h3>
            {extractedData.discounts.map((discount, idx) => (
              <div key={idx} className="discount-item">
                <span className="discount-desc">{discount.description}</span>
                <span className="discount-amount">-${Math.abs(discount.amount).toFixed(2)}</span>
              </div>
            ))}
            {extractedData.total_discounts && extractedData.total_discounts > 0 && (
              <div className="discount-total">
                <span>Total Savings</span>
                <span className="savings-amount">-${extractedData.total_discounts.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Free Bag Redemption Detection */}
        {extractedData?.free_bag_detection?.detected_redemptions &&
         extractedData.free_bag_detection.detected_redemptions.length > 0 && (
          <div className={`free-bag-alert ${extractedData.free_bag_detection.auto_redeem_eligible ? 'eligible' : 'warning'}`}>
            <span className="free-bag-icon">🎉</span>
            <div className="free-bag-content">
              <strong>Free Bag Redeemed!</strong>
              <p>
                {extractedData.free_bag_detection.detected_redemptions.length === 1
                  ? 'We detected a free bag redemption on this receipt.'
                  : `We detected ${extractedData.free_bag_detection.detected_redemptions.length} free bag redemptions.`}
              </p>
              {extractedData.free_bag_detection.has_free_bag_available ? (
                <span className="free-bag-status valid">
                  ✓ Your free bag reward will be marked as redeemed
                </span>
              ) : (
                <span className="free-bag-status warning">
                  ⚠ You don't have a free bag available - this discount won't be tracked
                </span>
              )}
            </div>
          </div>
        )}

        {/* Total */}
        <div className="receipt-total">
          <span>Total Paid</span>
          <span>${Number(extractedData?.total || 0).toFixed(2)}</span>
        </div>
        {extractedData?.red_bridge_total !== undefined && extractedData.red_bridge_total > 0 && (
          <div className="receipt-rb-total">
            <span>Red Bridge Total</span>
            <span>${Number(extractedData.red_bridge_total).toFixed(2)}</span>
          </div>
        )}

        {/* Bin Allocation Preview */}
        {extractedData?.bin_matches && extractedData.bin_matches.length > 0 && (
          <div className="bin-allocation-section">
            <h3>Feed Bin Updates</h3>
            {extractedData.bin_matches.map((match, idx) => {
              // Get category-specific icon
              const getCategoryIcon = (category?: string) => {
                const icons: Record<string, string> = {
                  'POULTRY': '🐔',
                  'DOG': '🐕',
                  'CAT': '🐱',
                  'HORSE': '🐴',
                  'SWINE': '🐷',
                  'GOAT': '🐐',
                  'SHEEP': '🐑',
                  'RABBIT': '🐰',
                  'CATTLE': '🐄',
                };
                return icons[category?.toUpperCase() || ''] || '📦';
              };
              const icon = getCategoryIcon(match.category);

              return (
                <div key={idx} className={`bin-match-item ${match.matched_bin_id ? 'matched' : 'unmatched'} ${!match.is_red_bridge ? 'non-rb' : ''}`}>
                  {match.matched_bin_id ? (
                    <>
                      <span className="bin-match-icon">{icon}</span>
                      <div className="bin-match-content">
                        <span className="bin-match-product">
                          {match.display_name || match.product_name}
                          {!match.is_red_bridge && <span className="non-rb-badge">non-RB</span>}
                        </span>
                        <span className="bin-match-action">
                          +{match.total_lbs} lbs → {match.matched_bin_name}
                        </span>
                      </div>
                    </>
                  ) : match.create_suggestion ? (
                    <>
                      <span className="bin-match-icon">{icon}</span>
                      <div className="bin-match-content">
                        <span className="bin-match-product">
                          {match.display_name || match.product_name}
                          {!match.is_red_bridge && <span className="non-rb-badge">non-RB</span>}
                        </span>
                        <span className="bin-match-action create">
                          Create bin: "{match.create_suggestion.suggested_name}"?
                        </span>
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {/* Loyalty Preview */}
        {redBridgeItems.length > 0 && (
          <div className="loyalty-preview">
            <span className="loyalty-icon">🎁</span>
            <span className="loyalty-text">
              {(() => {
                const totalBags = redBridgeItems.reduce((sum, item) => sum + item.quantity, 0);
                const freebagsRedeemed = extractedData?.free_bag_detection?.detected_redemptions?.length || 0;
                const loyaltyBags = Math.max(0, totalBags - freebagsRedeemed);
                if (loyaltyBags > 0) {
                  return `+${loyaltyBags} bag${loyaltyBags > 1 ? 's' : ''} toward your next reward!`;
                } else {
                  return 'Free bag redeemed - no loyalty points for this bag';
                }
              })()}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="confirm-actions">
          <button onClick={() => setStep('upload')} className="btn-secondary">
            Start Over
          </button>
          <button onClick={handleConfirm} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Confirm Purchase'}
          </button>
        </div>
      </div>
    );
  };

  const renderSuccessStep = () => (
    <div className="success-step">
      <div className="success-icon">✓</div>
      <h1>Purchase Logged!</h1>

      {savedResult?.bags_credited > 0 && (
        <div className="credit-banner">
          <span className="credit-icon">🎁</span>
          <span className="credit-text">
            +{savedResult.bags_credited} bag{savedResult.bags_credited > 1 ? 's' : ''} added to your loyalty!
          </span>
        </div>
      )}

      <p className="success-message">
        Your purchase has been recorded and your feed tracking has been updated.
      </p>

      {/* Suggested Animals - Based on feed purchased */}
      {savedResult?.suggested_animals?.length > 0 && (
        <div className="suggested-animals-section">
          <h3>Add Animals to Your Profile?</h3>
          {savedResult.suggested_animals.map((suggestion: any, idx: number) => (
            <div key={idx} className="suggested-animal-card">
              <div className="suggestion-icon">🐾</div>
              <div className="suggestion-content">
                <p className="suggestion-message">{suggestion.message}</p>
                <p className="suggestion-feed">Based on: {suggestion.feed_purchased}</p>
              </div>
              <button
                onClick={() => navigate('/bridge-club/animals/add', { state: { category: suggestion.category } })}
                className="btn-add-animal"
              >
                Add {suggestion.friendly_name}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Red Bridge Alternatives - For non-RB products */}
      {savedResult?.red_bridge_alternatives?.length > 0 && (
        <div className="alternatives-section">
          <h3>Try Red Bridge Feed!</h3>
          <p className="alternatives-intro">
            Earn loyalty rewards when you buy Red Bridge products.
          </p>
          {savedResult.red_bridge_alternatives.map((item: any, idx: number) => (
            <div key={idx} className="alternative-item">
              <p className="non-rb-product">
                Instead of <strong>{item.product_name}</strong>, try:
              </p>
              <div className="alternative-list">
                {item.red_bridge_alternatives.slice(0, 3).map((alt: any, altIdx: number) => (
                  <div key={altIdx} className="alternative-card">
                    <span className="alt-name">{alt.name}</span>
                    <span className="alt-grade">{alt.feed_grade}</span>
                    {alt.bag_price > 0 && (
                      <span className="alt-price">${Number(alt.bag_price || 0).toFixed(2)}/{alt.bag_size} lb bag</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="success-actions">
        <button onClick={() => navigate('/bridge-club/purchases')} className="btn-secondary">
          View Purchases
        </button>
        <button onClick={() => navigate('/bridge-club/dashboard')} className="btn-primary">
          Go to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <BridgeClubLayout hideNav={step === 'scanning'}>
      <div className="upload-page">
        {step === 'upload' && renderUploadStep()}
        {step === 'scanning' && renderScanningStep()}
        {step === 'confirm' && renderConfirmStep()}
        {step === 'success' && renderSuccessStep()}
      </div>

      {/* Celebration Modal - shown when 10-bag reward is earned */}
      {celebrationData && (
        <CelebrationModal
          isOpen={showCelebration}
          onClose={handleCloseCelebration}
          celebration={celebrationData}
        />
      )}
    </BridgeClubLayout>
  );
};

export default BridgeClubUploadReceipt;
